import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { StaffManager } from "@/components/StaffManager";
import { NotificationSettings } from "@/components/NotificationSettings";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CreditCard, Plus, Trash2, Save, Loader2, Star, Percent, Coins, Wallet, Info, Lock, SlidersHorizontal, RotateCcw, Car, Truck, Gift, Zap } from "lucide-react";

const CUR_SYMBOL = { eur: "€", usd: "$", gbp: "£", chf: "CHF", cad: "C$" };

const SECTION_META = {
  payments: { kicker: "// Paramètres · Paiements", title: "Paiements" },
  catalog: { kicker: "// Paramètres · Catalogue", title: "Catalogue & Prix" },
  services: { kicker: "// Paramètres · Catalogue", title: "Catalogue & Prix" },
  staff: { kicker: "// Paramètres · Équipe", title: "Staff & Permissions" },
  notifications: { kicker: "// Paramètres · Notifications", title: "Notifications" },
};

export const AdminSettings = ({ section = "payments" }) => {
  const [s, setS] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [truckIds, setTruckIds] = useState([]);
  const [currencies, setCurrencies] = useState(["eur"]);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const canPay = user?.is_owner || user?.permissions?.payments;
  const canStaff = user?.is_owner || user?.permissions?.staff;
  const meta = SECTION_META[section] || SECTION_META.payments;
  const needsSettings = section === "payments" || section === "catalog" || section === "services";

  useEffect(() => {
    if (!needsSettings) return;
    api.get("/admin/settings").then(({ data }) => {
      setS(data.settings);
      setCatalog(data.methods_catalog);
      setCurrencies(data.currencies);
      setServicesCatalog(data.services_catalog || []);
      setTruckIds(data.truck_service_ids || []);
    });
  }, [needsSettings]);

  const setField = (k, v) => setS((o) => ({ ...o, [k]: v }));
  const toggleMethod = (id) => setS((o) => ({ ...o, methods: { ...o.methods, [id]: !o.methods[id] } }));
  const setPromo = (k, v) => setS((o) => ({ ...o, promo: { ...(o.promo || {}), [k]: v } }));
  const setPack = (i, k, v) => setS((o) => { const p = [...o.packages]; p[i] = { ...p[i], [k]: v }; return { ...o, packages: p }; });
  const addPack = () => setS((o) => ({ ...o, packages: [...o.packages, { id: "", label: "NOUVEAU", credits: 10, discount_pct: 0, tag: "", popular: false, perks: [] }] }));
  const removePack = (i) => setS((o) => ({ ...o, packages: o.packages.filter((_, idx) => idx !== i) }));

  const [vclass, setVclass] = useState("car");
  const creditKey = vclass === "truck_agri" ? "service_credits_truck" : "service_credits";
  const fromKey = vclass === "truck_agri" ? "service_from_truck" : "service_from";

  const setServiceCredit = (id, v) => setS((o) => ({
    ...o,
    [creditKey]: { ...(o[creditKey] || {}), [id]: v === "" ? "" : Math.max(0, parseInt(v || "0")) },
  }));
  const setServiceCost = (id, v) => setS((o) => ({
    ...o,
    service_cost: { ...(o.service_cost || {}), [id]: v === "" ? "" : Math.max(0, parseFloat(v || "0")) },
  }));
  const resetServiceCredit = (id) => setS((o) => {
    const sc = { ...(o[creditKey] || {}) };
    delete sc[id];
    return { ...o, [creditKey]: sc };
  });
  const toggleServiceFrom = (id) => setS((o) => {
    const sf = { ...(o[fromKey] || {}) };
    if (sf[id]) delete sf[id]; else sf[id] = true;
    return { ...o, [fromKey]: sf };
  });
  const effCredit = (svc) => {
    const ov = s?.[creditKey]?.[svc.id];
    return (ov === "" || ov === undefined || ov === null) ? svc.default_credits : ov;
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const Header = ({ children }) => (
    <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
      <div>
        <div className="font-mono text-xs text-aurora-purple tracking-[0.3em] uppercase mb-2">{meta.kicker}</div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">{meta.title}</h1>
      </div>
      {children}
    </div>
  );

  const NoAccess = () => (
    <div className="glass rounded-md p-10 text-center" data-testid="settings-no-access">
      <Lock className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
      <p className="font-mono text-xs text-zinc-500 uppercase tracking-widest">Accès restreint — autorisation requise</p>
    </div>
  );

  // NOTIFICATIONS section (available to any admin)
  if (section === "notifications") {
    return (
      <AppShell mode="admin">
        <Header />
        <div className="max-w-2xl"><NotificationSettings /></div>
      </AppShell>
    );
  }

  // STAFF section
  if (section === "staff") {
    return (
      <AppShell mode="admin">
        <Header />
        {canStaff ? <StaffManager /> : <NoAccess />}
      </AppShell>
    );
  }

  // PAYMENTS / CATALOG sections need settings loaded
  if (!canPay) {
    return <AppShell mode="admin"><Header /><NoAccess /></AppShell>;
  }
  if (!s) {
    return <AppShell mode="admin"><div className="font-mono text-xs text-zinc-500">Chargement...</div></AppShell>;
  }

  const groups = [...new Set(catalog.map((m) => m.group))];
  const sym = CUR_SYMBOL[s.currency] || s.currency;

  const SaveBtn = (
    <button onClick={save} disabled={saving} data-testid="settings-save-btn"
      className="flex items-center gap-2 px-6 py-3 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm disabled:opacity-60">
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
    </button>
  );

  if (section === "catalog" || section === "services") {
    const creditValue = parseFloat(s.credit_eur_ref) || 0;
    const visibleServices = vclass === "truck_agri" ? servicesCatalog.filter((x) => truckIds.includes(x.id)) : servicesCatalog;
    const svcGroups = [...new Set(visibleServices.map((x) => x.group))];
    return (
      <AppShell mode="admin">
        <Header>{SaveBtn}</Header>

        <div className="glass rounded-md p-6 mb-5 relative overflow-hidden" data-testid="credit-value-card">
          <div className="m-stripe-bar absolute top-0 left-0" />
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-1 flex items-center gap-2">
            <Coins className="w-5 h-5 text-aurora-cyan" /> Valeur du crédit
          </h3>
          <p className="font-mono text-[10px] text-zinc-500 mb-5 max-w-2xl leading-snug">
            Définit la valeur de référence d'1 crédit en {sym}. Sert à calculer le prix indicatif en {sym} affiché aux clients pour chaque prestation.
          </p>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">1 crédit =</label>
              <div className="relative w-40">
                <input data-testid="credit-value-input" type="number" step="0.01" min="0.01" value={s.credit_eur_ref ?? ""}
                  onChange={(e) => setField("credit_eur_ref", e.target.value === "" ? "" : parseFloat(e.target.value))}
                  className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 font-mono text-lg focus:border-aurora-cyan focus:outline-none" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-sm">{sym}</span>
              </div>
            </div>
            <div className="font-mono text-[10px] text-zinc-500 pb-3">
              Ex : un service à <span className="text-aurora-cyan">10 crédits</span> ≈ <span className="text-white">{(10 * creditValue).toFixed(2)}{sym}</span>
            </div>
          </div>
        </div>

        <div className="glass rounded-md p-6 mb-5">
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-2 flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-aurora-cyan" /> Solutions & prix
          </h3>
          <p className="font-mono text-[10px] text-zinc-500 mb-5 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-px shrink-0" /> Toutes les solutions. Réglez les <span className="text-aurora-cyan">crédits</span> (prix client ≈ €), saisissez votre <span className="text-aurora-purple">coût</span> de revient — la <span className="text-aurora-cyan">marge atelier</span> se calcule automatiquement. S'applique aux nouvelles commandes.
          </p>

          <div className="flex gap-2 mb-5" data-testid="pricing-class-tabs">
            {[["car", "Voiture", Car], ["truck_agri", "Camion · Agricole", Truck]].map(([id, label, Ico]) => (
              <button key={id} data-testid={`pricing-class-${id}`} onClick={() => setVclass(id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-sm font-display font-bold uppercase tracking-wider text-xs transition-colors border ${vclass === id ? (id === "truck_agri" ? "bg-[#FFB020] text-black border-[#FFB020]" : "bg-aurora-cyan text-black border-aurora-cyan") : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
                <Ico className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          {vclass === "truck_agri" && (
            <div className="mb-4 p-3 rounded-sm border border-[#FFB020]/40 bg-[#FFB020]/5 font-mono text-[10px] text-[#FFB020] flex items-center gap-2">
              <Truck className="w-3.5 h-3.5 shrink-0" /> Grille Camion · Agricole — solutions limitées à <b className="text-white">FAP, EGR, AdBlue, Stage 1, DTC</b>. Laissez vide pour conserver le prix par défaut, ou saisissez vos tarifs poids lourd.
            </div>
          )}
          {svcGroups.map((g) => (
            <div key={g} className="mb-6 last:mb-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-aurora-purple mb-3">{g}</div>
              <div className="space-y-2">
                {visibleServices.filter((x) => x.group === g).map((svc) => {
                  const val = effCredit(svc);
                  const overridden = s?.[creditKey]?.[svc.id] !== undefined && s?.[creditKey]?.[svc.id] !== "";
                  const price = (parseInt(val || 0)) * creditValue;
                  const costRaw = s?.service_cost?.[svc.id];
                  const cost = (costRaw === "" || costRaw === undefined || costRaw === null) ? 0 : parseFloat(costRaw);
                  const margin = price - cost;
                  const priceFrom = !!s?.[fromKey]?.[svc.id];
                  return (
                    <div key={svc.id} data-testid={`service-row-${svc.id}`} className="grid grid-cols-12 gap-2 items-center bg-black/20 border border-white/5 rounded-sm p-3">
                      <div className="col-span-12 sm:col-span-4 min-w-0">
                        <div className="text-sm font-display uppercase tracking-tight truncate">{svc.name}</div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">défaut : {svc.default_credits} cr.{overridden && <span className="text-aurora-cyan ml-2">· modifié</span>}</div>
                      </div>
                      <div className="col-span-4 sm:col-span-2 relative">
                        <input
                          data-testid={`service-credit-${svc.id}`}
                          type="number" min="0" value={s?.[creditKey]?.[svc.id] ?? (vclass === "truck_agri" ? "" : svc.default_credits)} placeholder={svc.default_credits}
                          onChange={(e) => setServiceCredit(svc.id, e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-sm px-2 py-2 text-sm font-mono focus:border-aurora-cyan focus:outline-none" />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 font-mono text-[9px]">CR</span>
                      </div>
                      <div className="col-span-4 sm:col-span-2" data-testid={`service-eur-${svc.id}`}>
                        <div className="font-mono text-sm text-signal-success">
                          {priceFrom ? "à partir de " : "≈ "}{price.toFixed(2)}{sym}
                        </div>
                        <label className="flex items-center gap-1.5 mt-1 cursor-pointer w-fit" title="Afficher « à partir de » devant le prix côté client">
                          <input
                            type="checkbox"
                            data-testid={`service-from-${svc.id}`}
                            checked={priceFrom}
                            onChange={() => toggleServiceFrom(svc.id)}
                            className="accent-aurora-cyan w-3.5 h-3.5 cursor-pointer"
                          />
                          <span className={`font-mono text-[9px] uppercase tracking-widest ${priceFrom ? "text-aurora-cyan" : "text-zinc-500"}`}>À partir de</span>
                        </label>
                      </div>
                      <div className="col-span-4 sm:col-span-2 relative">
                        <input
                          data-testid={`service-cost-${svc.id}`}
                          type="number" min="0" step="0.01" placeholder="coût"
                          value={s?.service_cost?.[svc.id] ?? ""}
                          onChange={(e) => setServiceCost(svc.id, e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-sm px-2 py-2 text-sm font-mono focus:border-aurora-purple focus:outline-none" />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 font-mono text-[9px]">{sym}</span>
                      </div>
                      <div className="col-span-6 sm:col-span-1 font-mono text-[11px]" data-testid={`service-margin-${svc.id}`}
                        title="Marge atelier = prix € − coût">
                        <span className={margin >= 0 ? "text-aurora-cyan" : "text-signal-error"}>{margin >= 0 ? "+" : ""}{margin.toFixed(2)}{sym}</span>
                      </div>
                      <div className="col-span-6 sm:col-span-1 flex justify-end">
                        <button onClick={() => resetServiceCredit(svc.id)} title="Réinitialiser les crédits au défaut"
                          disabled={!overridden}
                          className="w-9 h-9 grid place-items-center rounded-sm border border-white/10 text-zinc-500 hover:border-aurora-cyan hover:text-aurora-cyan transition-colors disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:text-zinc-500">
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="glass rounded-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-aurora-cyan" /> Packs de crédits
            </h3>
            <button onClick={addPack} data-testid="add-pack-btn" className="flex items-center gap-1.5 px-3 py-2 border border-white/15 rounded-sm font-mono text-[11px] uppercase tracking-widest hover:border-aurora-cyan/60 transition-colors">
              <Plus className="w-3.5 h-3.5" /> Ajouter
            </button>
          </div>
          <p className="font-mono text-[10px] text-zinc-500 mb-5 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-px shrink-0" /> Le prix client suit automatiquement la <span className="text-aurora-cyan">valeur du crédit</span> (crédits × valeur). Ajoutez une <span className="text-aurora-purple">remise %</span> pour les gros packs. Le <span className="text-signal-success">prix final</span> est ce que voit le client.
          </p>
          <div className="hidden sm:grid grid-cols-12 gap-3 px-3 mb-2 font-mono text-[9px] uppercase tracking-widest text-zinc-600">
            <div className="col-span-3">Nom</div>
            <div className="col-span-2">Crédits</div>
            <div className="col-span-2">Remise %</div>
            <div className="col-span-2">Prix final</div>
            <div className="col-span-3 text-right">Options</div>
          </div>
          <div className="space-y-3">
            {s.packages.map((p, i) => {
              const base = (parseInt(p.credits || 0)) * creditValue;
              const disc = Math.max(0, Math.min(90, parseFloat(p.discount_pct || 0)));
              const finalPrice = base * (1 - disc / 100);
              return (
              <div key={i} data-testid={`pack-row-${i}`} className="grid grid-cols-12 gap-3 items-center bg-black/20 border border-white/5 rounded-sm p-3">
                <input value={p.label} onChange={(e) => setPack(i, "label", e.target.value)} placeholder="Nom" className="col-span-12 sm:col-span-3 bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm font-display uppercase focus:border-aurora-cyan focus:outline-none" />
                <div className="col-span-4 sm:col-span-2 relative">
                  <input type="number" data-testid={`pack-credits-${i}`} value={p.credits} onChange={(e) => setPack(i, "credits", parseInt(e.target.value || "0"))} className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-aurora-cyan focus:outline-none" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 font-mono text-[9px]">CR</span>
                </div>
                <div className="col-span-4 sm:col-span-2 relative">
                  <input type="number" step="0.1" min="0" max="90" data-testid={`pack-discount-${i}`} value={p.discount_pct ?? 0} onChange={(e) => setPack(i, "discount_pct", e.target.value === "" ? 0 : parseFloat(e.target.value))} className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-aurora-purple focus:outline-none" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 font-mono text-[9px]">%</span>
                </div>
                <div className="col-span-4 sm:col-span-2 font-mono text-sm" data-testid={`pack-price-${i}`}>
                  <span className="text-signal-success font-bold">{finalPrice.toFixed(2)}{sym}</span>
                  {disc > 0 && <span className="text-zinc-600 line-through ml-1.5 text-[11px]">{base.toFixed(2)}</span>}
                </div>
                <div className="col-span-12 sm:col-span-3 flex items-center gap-2">
                  <input value={p.tag || ""} onChange={(e) => setPack(i, "tag", e.target.value)} placeholder="Étiquette" className="flex-1 bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm focus:border-aurora-cyan focus:outline-none" />
                  <button onClick={() => setPack(i, "popular", !p.popular)} title="Populaire" className={`w-9 h-9 grid place-items-center rounded-sm border transition-colors shrink-0 ${p.popular ? "border-aurora-cyan bg-aurora-cyan/10 text-aurora-cyan" : "border-white/10 text-zinc-500"}`}>
                    <Star className={`w-4 h-4 ${p.popular ? "fill-aurora-cyan" : ""}`} />
                  </button>
                  <button onClick={() => removePack(i)} data-testid={`remove-pack-${i}`} title="Supprimer" className="w-9 h-9 grid place-items-center rounded-sm border border-white/10 text-zinc-500 hover:border-signal-error hover:text-signal-error transition-colors shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </AppShell>
    );
  }

  // PAYMENTS section (default)
  return (
    <AppShell mode="admin">
      <Header>{SaveBtn}</Header>

      <div className="glass rounded-md p-6 mb-5">
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-5 flex items-center gap-2">
          <Coins className="w-5 h-5 text-aurora-cyan" /> Général
        </h3>
        <div className="grid sm:grid-cols-2 gap-6 max-w-2xl">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Devise</label>
            <select data-testid="currency-select" value={s.currency} onChange={(e) => setField("currency", e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 font-display uppercase tracking-wide text-sm focus:border-aurora-cyan focus:outline-none">
              {currencies.map((c) => <option key={c} value={c}>{c.toUpperCase()} ({CUR_SYMBOL[c] || c})</option>)}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Percent className="w-3 h-3" /> Remise de bienvenue (1er achat)</label>
            <div className="relative">
              <input data-testid="discount-input" type="number" min="0" max="90" value={s.welcome_discount_pct}
                onChange={(e) => setField("welcome_discount_pct", parseInt(e.target.value || "0"))}
                className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 font-mono text-sm focus:border-aurora-cyan focus:outline-none" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-sm">%</span>
            </div>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Percent className="w-3 h-3" /> TVA B2C (%)</label>
            <div className="relative">
              <input data-testid="vat-rate-input" type="number" min="0" max="30" value={s.vat_rate ?? 21}
                onChange={(e) => setField("vat_rate", e.target.value === "" ? "" : parseInt(e.target.value))}
                className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 font-mono text-sm focus:border-aurora-cyan focus:outline-none" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-sm">%</span>
            </div>
            <p className="font-mono text-[9px] text-zinc-600 mt-1">B2B avec TVA intracom valide (VIES) = exonéré.</p>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Zap className="w-3 h-3 text-amber-300" /> SLA Tickets Express (min)</label>
            <div className="relative">
              <input data-testid="sla-express-input" type="number" min="1" max="1440" value={s.express_sla_minutes ?? 30}
                onChange={(e) => setField("express_sla_minutes", e.target.value === "" ? "" : parseInt(e.target.value))}
                className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 font-mono text-sm focus:border-amber-400 focus:outline-none" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-sm">min</span>
            </div>
            <p className="font-mono text-[9px] text-zinc-600 mt-1">Délai cible de 1ʳᵉ réponse. Le chrono d'un ticket Express vire au rouge au-delà.</p>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Zap className="w-3 h-3 text-aurora-cyan" /> Support Express · Seuil VIP (€)</label>
            <div className="relative">
              <input data-testid="express-vip-input" type="number" min="0" step="10" value={s.express_lifetime_eur ?? 250}
                onChange={(e) => setField("express_lifetime_eur", e.target.value === "" ? "" : parseFloat(e.target.value))}
                className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 font-mono text-sm focus:border-aurora-cyan focus:outline-none" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-sm">€</span>
            </div>
            <p className="font-mono text-[9px] text-zinc-600 mt-1">Achats cumulés ≥ ce montant → Support Express auto. 0 = désactivé.</p>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Zap className="w-3 h-3 text-aurora-cyan" /> Boost Express à l'unité (crédits)</label>
            <div className="relative">
              <input data-testid="express-boost-input" type="number" min="0" max="999" value={s.express_boost_credits ?? 8}
                onChange={(e) => setField("express_boost_credits", e.target.value === "" ? "" : parseInt(e.target.value))}
                className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 font-mono text-sm focus:border-aurora-cyan focus:outline-none" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-sm">cr.</span>
            </div>
            <p className="font-mono text-[9px] text-zinc-600 mt-1">Crédits dépensés pour passer 1 ticket en Express. 0 = boost désactivé.</p>
          </div>
        </div>
      </div>

      <div className="glass rounded-md p-6 mb-5 relative overflow-hidden" data-testid="promo-settings-card">
        <div className="m-stripe-bar absolute top-0 left-0" />
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <h3 className="font-display text-lg font-semibold uppercase tracking-tight flex items-center gap-2">
            <Gift className="w-5 h-5 text-aurora-cyan" /> Bannière promo (packs de crédits)
          </h3>
          <button data-testid="promo-toggle" onClick={() => setPromo("enabled", !s.promo?.enabled)}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm border font-display font-bold uppercase tracking-wider text-xs transition-colors ${s.promo?.enabled ? "border-signal-success/50 bg-signal-success/10 text-signal-success" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
            <div className={`w-9 h-5 rounded-full p-0.5 transition-colors ${s.promo?.enabled ? "bg-signal-success" : "bg-white/10"}`}>
              <div className={`w-4 h-4 rounded-full bg-black transition-transform ${s.promo?.enabled ? "translate-x-4" : ""}`} />
            </div>
            {s.promo?.enabled ? "Active" : "Inactive"}
          </button>
        </div>
        <p className="font-mono text-[10px] text-zinc-500 mb-5 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 mt-px shrink-0" /> Affiche une bannière en haut de la page Crédits du client et applique une <span className="text-aurora-cyan">remise réelle</span> sur tous les packs tant qu'elle est active (jusqu'à la date de fin).
        </p>
        <div className="grid sm:grid-cols-2 gap-5 max-w-3xl">
          <div className="sm:col-span-2">
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Message affiché au client</label>
            <input data-testid="promo-text" value={s.promo?.text || ""} onChange={(e) => setPromo("text", e.target.value)}
              placeholder="Ex. -15% sur tous les packs ce week-end !"
              className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-sm focus:border-aurora-cyan focus:outline-none" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Percent className="w-3 h-3" /> Remise appliquée</label>
            <div className="relative">
              <input data-testid="promo-discount" type="number" min="0" max="90" value={s.promo?.discount_pct ?? 0}
                onChange={(e) => setPromo("discount_pct", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 font-mono text-sm focus:border-aurora-cyan focus:outline-none" />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-sm">%</span>
            </div>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Date de fin (expire automatiquement)</label>
            <input data-testid="promo-ends-at" type="datetime-local" value={(s.promo?.ends_at || "").slice(0, 16)}
              onChange={(e) => setPromo("ends_at", e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 font-mono text-sm focus:border-aurora-cyan focus:outline-none" />
          </div>
        </div>
      </div>

      <div className="glass rounded-md p-6">
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-2 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-aurora-cyan" /> Moyens de paiement
        </h3>
        <p className="font-mono text-[10px] text-zinc-500 mb-5 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 mt-px shrink-0" /> Activez les moyens à proposer. Les méthodes "Stripe" s'affichent au paiement selon votre configuration Stripe ; les méthodes externes (PayPal, Crypto, virement) seront ajoutées au flux dédié.
        </p>
        {groups.map((g) => (
          <div key={g} className="mb-5 last:mb-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">{g}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {catalog.filter((m) => m.group === g).map((m) => {
                const on = !!s.methods[m.id];
                return (
                  <button key={m.id} onClick={() => toggleMethod(m.id)} data-testid={`method-${m.id}`}
                    className={`flex items-center justify-between gap-3 p-4 rounded-sm border text-left transition-colors ${on ? "border-aurora-cyan/50 bg-aurora-cyan/5" : "border-white/10 hover:border-white/25"}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-display uppercase tracking-tight truncate">{m.label}</div>
                      {!m.stripe && <div className="font-mono text-[9px] uppercase tracking-widest text-aurora-purple mt-0.5">Flux externe</div>}
                    </div>
                    <div className={`w-10 h-6 rounded-full p-0.5 shrink-0 transition-colors ${on ? "bg-aurora-cyan" : "bg-white/10"}`}>
                      <div className={`w-5 h-5 rounded-full bg-black transition-transform ${on ? "translate-x-4" : ""}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </AppShell>
  );
};
